import { CardContent, LinearProgress, Stack, Typography } from "@mui/material"
import { StatusMessageProps } from "components/BatchUpdate"
import MoreInfo from "components/MoreInfo"
import {
	DhwSchedulerInput,
	PvtHeatPumpDhwSchedulerFragment,
	PvtHeatPumpDhwSchedulerFragmentDoc,
	UpdatePvtHeatPumpDhwSchedulerMutation,
	UpdatePvtHeatPumpDhwSchedulerMutationVariables,
	useReadHeatPumpDhwSchedulerQuery,
	useUpdatePvtHeatPumpDhwSchedulerMutation,
} from "generated/graphql"
import useForm from "hooks/useForm"

import BooleanInput from "inputs/BooleanInput"
import { useMessage } from "providers/MessageProvider"
import { useTranslation } from "react-i18next"
import onlyChangedValues from "tools/onlyChangedValues"
import FloatingFab from "components/FloatingFab"
import { Save } from "@mui/icons-material"
import appendErrorMessage from "tools/appendErrorMessage"
import handleSettingsError from "tools/handleSettingsError"
import { ApolloCache, InMemoryCache } from "@apollo/client"
import { GraphQLError } from "graphql"
import TimeInput from "inputs/TimeInput"
import { isPvtHeatPumpFirmwareNewerOrEqual, PVT_HEAT_PUMP_V1_4 } from "settings/firmwareVersions"
import CustomCard from "components/CustomCard"
import { useImperativeHandle, ForwardedRef, forwardRef } from "react"

function updatePvtHeatPumpCache(
	cache: ApolloCache<InMemoryCache>,
	{
		data,
		errors,
	}: { data?: UpdatePvtHeatPumpDhwSchedulerMutation | null; errors?: readonly GraphQLError[] | undefined },
	{ variables }: { variables?: UpdatePvtHeatPumpDhwSchedulerMutationVariables },
) {
	if (!data || !variables) return

	const { data: inputData, interfaceIds } = variables
	const ids = Array.isArray(interfaceIds) && interfaceIds.length > 0 ? interfaceIds : []

	const interfaceIdsWithErrors = errors
		?.filter((error) => typeof error.extensions.interfaceID === "string")
		.map((error) => error.extensions.interfaceID)

	const onlineInterfaceIds = ids.filter((id) => !interfaceIdsWithErrors?.includes(id))

	onlineInterfaceIds.forEach((defaultInterfaceId) => {
		const id = `PvtHeatPumpData:${defaultInterfaceId}`
		const oldPvtHeatPump = cache.readFragment<PvtHeatPumpDhwSchedulerFragment>({
			id,
			fragment: PvtHeatPumpDhwSchedulerFragmentDoc,
			fragmentName: "PvtHeatPumpDhwScheduler",
		})

		if (!oldPvtHeatPump?.dhwScheduler) return
		const newPvtHeatPump = (Object.keys(oldPvtHeatPump.dhwScheduler) as Array<keyof DhwSchedulerInput>).reduce(
			(acc, key) => ({
				...acc,
				[key]:
					inputData[key as keyof DhwSchedulerInput] ?? oldPvtHeatPump!.dhwScheduler![key as keyof DhwSchedulerInput],
			}),
			{} as DhwSchedulerInput,
		)

		cache.writeFragment<PvtHeatPumpDhwSchedulerFragment>({
			id,
			fragment: PvtHeatPumpDhwSchedulerFragmentDoc,
			fragmentName: "PvtHeatPumpDhwScheduler",
			data: { ...oldPvtHeatPump, dhwScheduler: newPvtHeatPump },
		})
	})
}

type Props = {
	defaultInterfaceId: string
	batchInterfaceIds?: string[]
	isPartialSave?: boolean
	handleSavingMessages?: (updateStatus: StatusMessageProps[]) => void | undefined
	handleSaveLoading?: (isLoading: boolean) => void
}

export type DHWSchedulerSettingsRef = {
	handleSave: () => Promise<void>
}

function DHWSchedulerSettingsInner(
	{ defaultInterfaceId, batchInterfaceIds, isPartialSave, handleSavingMessages, handleSaveLoading }: Props,
	ref: ForwardedRef<DHWSchedulerSettingsRef> | undefined,
) {
	const { t } = useTranslation(["heatPumpSettingsPage", "general", "heatPumpErrorCard"])
	const message = useMessage()
	const { register, submit, reset, fields } = useForm<DhwSchedulerInput>({}, handleSave)
	const isBatchUpdate = Array.isArray(batchInterfaceIds) && batchInterfaceIds.length > 0

	const { data, loading } = useReadHeatPumpDhwSchedulerQuery({
		variables: { interfaceId: defaultInterfaceId },
		onCompleted: (data) => {
			if (data.interface?.pvtHeatPump?.dhwScheduler) {
				reset(data.interface.pvtHeatPump.dhwScheduler)
			}
		},
	})
	const currentHPVersion = data?.interface?.pvtHeatPump?.firmwareVersion ?? Infinity // if unknown, pick high firmware version number

	const [updateSettings, { loading: loadingSave }] = useUpdatePvtHeatPumpDhwSchedulerMutation({
		update: updatePvtHeatPumpCache,
	})

	const changedFields = onlyChangedValues(data?.interface?.pvtHeatPump?.dhwScheduler ?? {}, fields)

	async function handleSave() {
		if ((isPartialSave || isPartialSave === undefined) && Object.entries(changedFields).length === 0) {
			message.info(t("heatPumpSettingsPage:Errors.HPHasTheseSettings"))
			return
		}

		if (isBatchUpdate) {
			message.info(t("general:SavingSettingsPleaseWait"))
		}

		try {
			const response = await updateSettings({
				variables: {
					interfaceIds: isBatchUpdate ? batchInterfaceIds : [defaultInterfaceId],
					data: isBatchUpdate ? (isPartialSave ? changedFields : fields) : changedFields,
				},
				errorPolicy: isBatchUpdate ? "all" : "none", // If 1 or more interface is offline, the mutation will continue, This will ignore the error catch for GraphQL
			})

			if (handleSavingMessages) {
				handleSavingMessages([{ success: true }])
			}

			// Since we use errorPolicy: "all", the error won't return at catch error
			if (response?.errors) {
				handleSettingsError({ errorResponse: response.errors, handleSavingMessages, t })
			}
		} catch (e) {
			message.error(appendErrorMessage(t("heatPumpSettingsPage:Errors.UnkownSaving"), e))
		}

		message.success(t("heatPumpSettingsPage:SettingsSaved"))

		if (handleSaveLoading) {
			handleSaveLoading(loadingSave)
		}
	}

	useImperativeHandle(ref, () => ({
		handleSave,
	}))

	if (loading) {
		return <LinearProgress />
	}
	if (!isPvtHeatPumpFirmwareNewerOrEqual(currentHPVersion, PVT_HEAT_PUMP_V1_4))
		return (
			<CustomCard>
				<CardContent>
					<Typography variant="h5" color="error">
						{t("DhwScheduleFirmwareTooOld", {
							firmwareVersion: data?.interface?.pvtHeatPump?.firmwareVersion
								? ` V${data.interface.pvtHeatPump.firmwareVersion.toFixed(1)}`
								: "",
						})}
					</Typography>
				</CardContent>
			</CustomCard>
		)

	return (
		<>
			<Stack direction="column" alignItems="left" spacing={2} mt={2}>
				<Stack direction="row" alignItems="center">
					<BooleanInput
						label={t("DhwScheduleEnable")}
						disabled={loading}
						{...register("schedulerEnabled")}
						fieldIsChanged={"schedulerEnabled" in changedFields}
					/>
					<MoreInfo>{t("DhwSheduleInfo")}</MoreInfo>
				</Stack>
				{fields.schedulerEnabled && (
					<>
						<Stack direction="row" alignItems="center" spacing={2}>
							<BooleanInput
								label={t("DhwScheduleBlockEnable")}
								disabled={loading}
								{...register("blockDHWEnabled")}
								fieldIsChanged={"blockDHWEnabled" in changedFields}
							/>
							{fields.blockDHWEnabled && (
								<>
									<TimeInput
										label={t("DhwScheduleBlockOnTime")}
										disabled={loading}
										fullWidth={false}
										{...register("blockOnTime")}
										fieldIsChanged={"blockOnTime" in changedFields}
									/>
									<TimeInput
										label={t("DhwScheduleBlockOffTime")}
										disabled={loading}
										fullWidth={false}
										{...register("blockOffTime")}
										fieldIsChanged={"blockOffTime" in changedFields}
									/>
								</>
							)}
							<MoreInfo>{t("DhwScheduleBlockInfo")}</MoreInfo>
						</Stack>
						<Stack direction="row" alignItems="center" spacing={2}>
							<BooleanInput
								label={t("DhwScheduleForceEnable")}
								disabled={loading}
								{...register("forceDHWEnabled")}
								fieldIsChanged={"forceDHWEnabled" in changedFields}
							/>
							{fields.forceDHWEnabled && (
								<>
									<TimeInput
										label={t("DhwScheduleForceOnTime")}
										disabled={loading}
										fullWidth={false}
										{...register("forceOnTime")}
										fieldIsChanged={"forceOnTime" in changedFields}
									/>
									<TimeInput
										label={t("DhwScheduleForceOffTime")}
										disabled={loading}
										fullWidth={false}
										{...register("forceOffTime")}
										fieldIsChanged={"forceOffTime" in changedFields}
									/>
								</>
							)}
							<MoreInfo>{t("DhwScheduleForceInfo")}</MoreInfo>
						</Stack>
					</>
				)}
			</Stack>
			{ref == null && (
				<FloatingFab submit={submit} loading={loading} fitBottom={isBatchUpdate} batchLoading={loadingSave}>
					<Save />
					<Typography variant="button">{t("DhwScheduleSave")}</Typography>
				</FloatingFab>
			)}
		</>
	)
}

export default forwardRef<DHWSchedulerSettingsRef, Props>(DHWSchedulerSettingsInner)
